"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";

import { useStudio } from "@/contexts/studio-context";
import { ApiError, inspectMidi, uploadMidi, uploadSheetMusic } from "@/lib/api/api-client";
import { useAuth } from "@/contexts/auth-context";
import { useAccountModal } from "@/contexts/account-modal-context";
import { recordPendingClaim } from "@/lib/library/pending-claims";
import { createSongFromAnalysis } from "@/lib/music/song-factory";
import { SongControler } from "@/lib/track/controler/song-controler";
import { decidePrefill, type TitleBadgeState } from "@/lib/api/title-suggestion";
import {
  trackAbandoned,
  resolveNeedsHelpReason,
} from "@/lib/telemetry/midi-quality-events";
import { newUuidV7, track, type UploadFailureReason, type UploadKind } from "@/lib/analytics";
import {
  MUSESCORE_EXPORT_MESSAGE,
  classifyUploadFile,
  humanizeFilename,
  isMuseScoreFile,
  type ScoreFormat,
  type UploadFileKind,
} from "@/lib/upload/score-file";
import { scopeQualityToFormat } from "@/lib/upload/quality-scope";
import { resolveOkWithHintsToastMessage } from "@/lib/upload/ok-with-hints-wording";
import type {
  AnalysisKey,
  InspectResult,
  QualityFlag,
  WorkRights,
} from "@/lib/music/analyzer-types";

import { StepDots } from "../../atoms/step-dots";
import { ModalTemplate } from "../../templates/modal-template";
import {
  formatFileSize,
  uploadSource,
  type Upload,
} from "../../molecules/file-drop-zone";

import { StepFile } from "./step-file";
import { StepMeta } from "./step-meta";
import { LaunchingOverlay } from "./launching-overlay";
import { UploadHelpScreen } from "./upload-help-screen";

/**
 * Minimum duration of the launching overlay. Sized so the 5-step diamond
 * cascade (5 × 600 ms) plays in full and still feels unhurried, even when
 * the API responds almost instantly.
 */
const ANALYSIS_LOADER_MIN_MS = 3800;

/**
 * Four-phase state machine driving the modal body:
 * - `drop`      : initial drop-zone, no file yet.
 * - `analyzing` : inspect step running (auto title, key, quality).
 * - `ready`     : metadata is editable, user can launch.
 * - `launching` : full-screen overlay, analyze + load underway.
 */
type Phase = "drop" | "analyzing" | "ready" | "launching";

/** Visible step labels in the modal header. The launching overlay hides them. */
const STEPS = ["Fichier", "Détails"];

/**
 * Shown when nothing in the dropped selection could be read. Names the
 * formats rather than the failure: the user needs the way out, not a verdict
 * on the file they chose.
 */
const UNSUPPORTED_FILE_MESSAGE =
  "On ne sait pas lire ce fichier. Dépose un MIDI, un MusicXML, un PDF ou des photos de ta partition.";

/**
 * Modal title per source family. A MusicXML is a score, but so are the
 * photos, so the wording has to name the file and not just the object.
 */
const MODAL_TITLES: Record<UploadFileKind, string> = {
  midi: "Importer un MIDI",
  musicxml: "Importer un fichier MusicXML",
  image: "Importer une partition",
};

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called once the song is loaded.
   * - `midiFileId`: persisted MIDI id (null for image uploads).
   * - `hints`     : when the inspect step returned `ok_with_hints`, the
   *                 active quality flags. Consumers can route these to a
   *                 non-blocking toast at the studio level.
   */
  onSongLoaded?: (
    songControler: SongControler,
    midiFileId: string | null,
    hints?: QualityFlag[],
  ) => void;
  /**
   * Optional file to pre-load when the modal opens. Used by the window-wide
   * drop zone, the file is dropped outside the modal, then the modal opens
   * and skips the file picker step. Only applied once per `open=true`
   * transition.
   */
  initialFile?: File | null;
}

export function UploadModal({
  open,
  onClose,
  onSongLoaded,
  initialFile,
}: UploadModalProps) {
  const { studio } = useStudio();
  const { isAuthenticated } = useAuth();
  const { openAccountModal } = useAccountModal();

  /**
   * Tracks whether we already consumed `initialFile` for the current
   * `open=true` cycle. Without this, the prefill effect would re-fire on
   * every render while the modal is open.
   */
  const consumedInitialFileRef = useRef<File | null>(null);

  const [phase, setPhase] = useState<Phase>("drop");
  const [upload, setUpload] = useState<Upload | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Inspect result driving both the `needs_help` routing and the
   * `ok_with_hints` warning surface. Kept here so the resolver and the
   * launching phase can both read it.
   */
  const [inspectResult, setInspectResult] = useState<InspectResult | null>(
    null,
  );

  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [tempo, setTempo] = useState(120);
  const [detectedKey, setDetectedKey] = useState<
    AnalysisKey | "loading" | null
  >(null);
  const [titleBadge, setTitleBadge] = useState<TitleBadgeState>("none");
  const [artistBadge, setArtistBadge] = useState<TitleBadgeState>("none");
  const [suggestedContext, setSuggestedContext] = useState<string | null>(null);
  /*
    Le verdict de droits, capté à l'inspection.

    Sans condition de confiance, contrairement au contexte suggéré juste
    au-dessus : le serveur a déjà refusé de conclure quand la résolution du
    compositeur n'était pas franche, donc ce qui arrive ici est soit sûr, soit
    absent. Le filtrer une seconde fois côté client ne ferait que masquer des
    verdicts valides.
  */
  const [workRights, setWorkRights] = useState<WorkRights | null>(null);
  /**
   * Real MIDI track indices the user picked on the `needs_help` screen, or
   * null when the auto-detection is trusted. Forwarded to the analyze call so
   * the analyzer keeps (and re-hands) only those tracks.
   */
  const [selectedTracks, setSelectedTracks] = useState<number[] | null>(null);
  /*
    Identifiant opaque de l'import en cours, tiré à la réception du fichier.

    Il joint entre eux les événements de qualité MIDI, qui portaient jusqu'ici
    le nom du fichier de la personne. Tiré ici et nulle part ailleurs : l'écran
    d'aide émet sept de ces événements et cette modale le huitième (l'abandon),
    or deux tirages indépendants ne se joindraient jamais — le champ perdrait
    exactement ce pour quoi il existe.

    En état et non en ref, bien qu'il ne serve qu'à la mesure : il descend en
    prop, donc il est lu au rendu, et lire un ref au rendu est exactement ce que
    `react-hooks/refs` refuse.

    `newUuidV7` et non `crypto.randomUUID` : celui-ci n'existe pas hors contexte
    sécurisé (l'application empaquetée), où son absence figeait tout le module
    au chargement.
  */
  const [importId, setImportId] = useState<string | null>(null);

  const songControlerRef = useRef<SongControler | null>(null);
  // Refs (not state) so the late `inspectMidi` resolver can read the
  // up-to-date edited-flag without re-binding the callback on every keystroke.
  const userHasEditedTitleRef = useRef(false);
  const userHasEditedArtistRef = useRef(false);
  /**
   * Monotonic id of the current inspect request. Bumped on every new file
   * (or on `Retour`), checked by the resolver to drop stale responses that
   * would otherwise stomp the UI back into `ready` after the user left.
   */
  const inspectRequestIdRef = useRef(0);

  const mode = upload?.mode ?? null;

  const resetState = useCallback(() => {
    setPhase("drop");
    setUpload(null);
    setError(null);
    setInspectResult(null);
    setTitle("");
    setArtist("");
    setTempo(120);
    setDetectedKey(null);
    setTitleBadge("none");
    setArtistBadge("none");
    setSuggestedContext(null);
    setSelectedTracks(null);
    userHasEditedTitleRef.current = false;
    userHasEditedArtistRef.current = false;
  }, []);

  // Wrap the title setter to record manual edits. Clearing the field counts
  // as an explicit edit (anti-stomp barrier).
  const handleTitleChange = useCallback((value: string) => {
    userHasEditedTitleRef.current = true;
    setTitleBadge("none");
    setSuggestedContext(null);
    setTitle(value);
  }, []);

  const handleArtistChange = useCallback((value: string) => {
    userHasEditedArtistRef.current = true;
    setArtistBadge("none");
    setArtist(value);
  }, []);

  const handleScoreFile = useCallback((f: File, format: ScoreFormat) => {
    const requestId = ++inspectRequestIdRef.current;

    setImportId(newUuidV7());

    setUpload({ mode: "score", format, file: f });
    setError(null);
    setInspectResult(null);
    // Slug-humanized fallback. Not flagged as a user edit so the late title
    // suggestion is allowed to overwrite it.
    const name = humanizeFilename(f.name);

    setTitle(name);
    setArtist("");
    setTitleBadge("none");
    setArtistBadge("none");
    setSuggestedContext(null);
    userHasEditedTitleRef.current = false;
    userHasEditedArtistRef.current = false;
    setDetectedKey("loading");
    setPhase("analyzing");

    // Fire-and-forget: inspect runs the key detection AND the title normalizer
    // in a single round-trip. We don't await it, the user can already start
    // editing; if they do, `userHasEdited*Ref` will block the pre-fill.
    inspectMidi(f, { filename: f.name })
      .then((result) => {
        // Drop the response if the user moved on (Retour, new file…).
        if (inspectRequestIdRef.current !== requestId) {
          return;
        }

        setDetectedKey(result.key ?? null);
        // The verdict is stored already scoped to the format: an engraved
        // score cannot be guilty of a performance MIDI's defects, and every
        // surface downstream (help screen, warning block, quality diamonds,
        // studio toast) reads this one field.
        setInspectResult({
          ...result,
          quality: scopeQualityToFormat(result.quality, format),
        });

        const decision = decidePrefill({
          response: result,
          userHasEditedTitle: userHasEditedTitleRef.current,
          userHasEditedArtist: userHasEditedArtistRef.current,
        });

        if (decision.nextTitle !== null) {
          setTitle(decision.nextTitle);
        }

        if (decision.nextArtist !== null) {
          setArtist(decision.nextArtist);
        }

        setTitleBadge(decision.titleBadge);
        setArtistBadge(decision.artistBadge);

        // Display the broader-work context only while the title is still the
        // suggestion (cleared as soon as the user edits, see handleTitleChange).
        setWorkRights(result.workRights ?? null);

        if (decision.titleBadge !== "none" && result.suggestedContext) {
          setSuggestedContext(result.suggestedContext);
        } else {
          setSuggestedContext(null);
        }

        // Always transition to `ready`. The help-screen variant is gated by
        // `showHelpScreen` (see derived rendering below) so the header/footer
        // shape stays consistent.
        setPhase("ready");
      })
      .catch((err: unknown) => {
        if (inspectRequestIdRef.current !== requestId) {
          return;
        }

        // Le forfait n'a plus d'import à donner : le dire tout de suite, plutôt
        // qu'au bout du formulaire, où l'import serait refusé pour la même raison.
        if (err instanceof ApiError && err.status === 429) {
          setError(err.message);
        }

        setDetectedKey(null);
        setInspectResult(null);
        // Even on inspect failure, the user can still launch the analyze
        // step — we just don't surface a quality verdict.
        setPhase("ready");
      });
  }, []);

  const handleImageFiles = useCallback((files: File[]) => {
    setImportId(newUuidV7());

    setUpload({ mode: "images", files });
    setError(null);
    setInspectResult(null);
    const name = humanizeFilename(files[0].name);

    setTitle(name);
    setArtist("");
    setTitleBadge("none");
    setArtistBadge("none");
    setSuggestedContext(null);
    userHasEditedTitleRef.current = false;
    userHasEditedArtistRef.current = false;
    setDetectedKey(null);
    // Image / OCR mode has no inspect endpoint yet — go straight to ready.
    setPhase("ready");
  }, []);

  const handleUnsupportedFiles = useCallback((files: File[]) => {
    // A MuseScore project is the one refusal with a next step to give. The
    // generic wording would send the user looking for another file when the
    // one they have only needs exporting.
    setError(
      files.some(isMuseScoreFile) ? MUSESCORE_EXPORT_MESSAGE : UNSUPPORTED_FILE_MESSAGE,
    );
  }, []);

  const handleClear = useCallback(() => {
    // Invalidate any in-flight inspect — its resolver would otherwise jump
    // the user back to `ready` once it lands.
    inspectRequestIdRef.current += 1;
    setUpload(null);
    setError(null);
    setDetectedKey(null);
    setInspectResult(null);
    setPhase("drop");
  }, []);

  // Consume `initialFile` when the modal opens with a pre-selected file.
  // Images and PDFs go through the OCR path, score files straight to the
  // analyzer — same routing as the in-modal drop zone.
  useEffect(() => {
    if (!open) {
      consumedInitialFileRef.current = null;

      return;
    }

    if (!initialFile) {
      return;
    }

    if (consumedInitialFileRef.current === initialFile) {
      return;
    }

    consumedInitialFileRef.current = initialFile;

    const kind = classifyUploadFile(initialFile);

    if (kind === null) {
      handleUnsupportedFiles([initialFile]);

      return;
    }

    if (kind === "image") {
      handleImageFiles([initialFile]);

      return;
    }

    handleScoreFile(initialFile, kind);
  }, [
    open,
    initialFile,
    handleScoreFile,
    handleImageFiles,
    handleUnsupportedFiles,
  ]);

  const handleAnalyze = useCallback(async () => {
    if (!upload || !studio) {
      return;
    }

    // Seule la branche image est concernee. Deposer une partition en image ou
    // en PDF declenche une rasterisation puis un passage d OCR par page, et
    // cette route est passee sous compte le 2026-08-26 ; l import MIDI, lui,
    // reste ouvert, c est le premier geste du studio sans compte.
    //
    // Avant `setPhase` et avant `upload_started` : sans quoi l entonnoir
    // enregistrerait un import commence qui n a jamais eu lieu.
    if (upload.mode !== "score" && !isAuthenticated) {
      openAccountModal("sheet_import");

      return;
    }

    setError(null);
    setPhase("launching");

    const startedAt = Date.now();
    // Vrai dès que l'analyse est revenue. Sans ce repère, l'échec du montage
    // local se rangerait sous `network` faute de mieux et l'entonnoir
    // accuserait le tuyau pour une faute qui est la nôtre.
    let answered = false;
    // MIDI and MusicXML share the pipeline but not the story: an engraved
    // score arrives with its hands written, so the two are worth telling
    // apart in the funnel.
    const uploadKind: UploadKind =
      upload.mode === "score" ? upload.format : "image";

    track({ name: "upload_started", properties: { kind: uploadKind } });

    try {
      if (songControlerRef.current) {
        songControlerRef.current.destroy();
        songControlerRef.current = null;
      }

      const apiResponse =
        upload.mode === "score"
          ? await uploadMidi(upload.file, {
              filename: upload.file.name,
              title: title || undefined,
              composer: artist || undefined,
              ...(selectedTracks ? { tracks: selectedTracks } : {}),
            })
          : await uploadSheetMusic(upload.files, {
              title: title || upload.files[0].name,
              composer: artist || undefined,
              ...(tempo > 0 ? { tempo } : {}),
            });

      // Passé ce point, le serveur a rendu son analyse : un échec après coup
      // n'accuse plus ni le réseau ni l'API, mais notre propre montage.
      answered = true;

      const song = createSongFromAnalysis(apiResponse);

      // Mesuré ici et pas après l'attente cosmétique de l'overlay : c'est le
      // temps réel d'import et d'analyse qui intéresse, pas la durée perçue.
      track({
        name: "upload_completed",
        properties: {
          kind: uploadKind,
          durationMs: Date.now() - startedAt,
          trackCount: song.getParts().length,
          noteCount: song.getParts().reduce((total, part) => total + part.getRawNotes().length, 0),
        },
      });

      // The analyzer doesn't echo the context back through the upload
      // response, it lives only in the inspect step's title-normalization
      // payload. We carry the suggested value forward so consumers
      // (PieceOverview hero) can render it without an extra round trip.
      if (suggestedContext !== null) {
        song.setContext(suggestedContext);
      }

      const songControler = new SongControler(song, studio.getTransport(), studio.getAudioContext());
      await songControler.load();

      studio.addTrack(songControler);
      songControlerRef.current = songControler;

      // Seek to the first note of the song
      const firstTick = Math.min(
        ...song.getParts().map((p) => p.getRawNotes()[0]?.tick ?? Infinity),
      );

      if (firstTick < Infinity) {
        studio.getLocalTransport().seekToTick(firstTick);
      }

      /*
        L'import est le seul chemin d'ouverture que `loadPieceFromCatalog` ne
        traverse pas : le morceau est monté ici, dans le studio, et jamais
        rechargé derrière. Retirer cet émetteur emporterait donc `upload` et
        `ocr` avec lui — les deux seules provenances qu'aucune autre surface
        n'émet — et la moitié importée du produit sortirait de la mesure. Il
        reste, mais aligné sur l'autre émetteur, sinon la promesse d'un
        événement unique décrivant le même instant sur la même clé est fausse.

        `apiResponse.piece.id`, et non `midiFileId` : c'est l'identité de
        **catalogue**, celle que `/api/pieces/{id}/song` sait rouvrir et celle
        que porte `piece_opened` partout ailleurs. Les deux espaces se
        mélangeaient sur la même propriété selon la source, si bien qu'un même
        morceau importé puis rouvert comptait pour deux, et qu'aucune jointure
        sur `pieceId` ne tenait. `midiFileId` reste utile — il sert la
        revendication d'import juste en dessous — mais il n'identifie pas une
        œuvre.

        Déplacé après le montage : émis dès l'analyse revenue, il annonçait une
        ouverture qu'un `load()` en échec démentait aussitôt, et le même import
        rendait alors `piece_opened` **et** `upload_failed`. Ici, il ne parle
        que d'un morceau réellement posé dans le studio, comme celui du
        catalogue.

        Sans `title` : il venait de la frappe de la personne ou du nom de son
        fichier, donc du contenu rédigé, expédié à un sous-traitant et conservé
        plus d'un an. Le titre reste en base chez nous, rattaché à `pieceId`.
      */
      track({
        name: "piece_opened",
        properties: {
          source: upload.mode === "score" ? "upload" : "ocr",
          pieceId: apiResponse.piece.id,
          // Pas de `loadMs`. Il mesure ailleurs la seule attente d'ouverture ;
          // ici, l'horloge tourne depuis l'envoi du fichier et engloberait
          // l'import et l'analyse, déjà mesurés par `upload_completed.durationMs`.
          // Le renseigner ferait d'une moyenne toutes provenances confondues
          // une moyenne d'imports, gonflée d'un ordre de grandeur.
        },
      });

      // Hold the launching overlay long enough for its cascade to read,
      // even when the API/processing finished in less than that budget.
      const elapsed = Date.now() - startedAt;
      const remaining = ANALYSIS_LOADER_MIN_MS - elapsed;

      if (remaining > 0) {
        await new Promise((resolve) => setTimeout(resolve, remaining));
      }

      // Forward the `ok_with_hints` flags so the studio can surface the
      // non-blocking toast described in midi-quality-feedback §3.1.
      const hintsToForward =
        inspectResult?.quality.kind === "ok_with_hints"
          ? inspectResult.quality.hints
          : undefined;

      // Note l import pour pouvoir le revendiquer a la creation du compte.
      // Sans compte, c est la seule trace qui rattache ce fichier a cette
      // personne ; sans elle, l inscription ne recupererait rien et la
      // promesse « tes imports rejoindront ta liste » serait vide.
      //
      // Sans effet quand on est deja connecte : l entree existe alors deja
      // cote serveur.
      if (apiResponse.midiFileId !== null) {
        recordPendingClaim(
          apiResponse.midiFileId,
          apiResponse.piece.title,
          isAuthenticated,
        );
      }

      onSongLoaded?.(songControler, apiResponse.midiFileId, hintsToForward);

      onClose();
      resetState();
    } catch (err) {
      // Le taux d'échec d'import est la métrique de santé la plus lourde de
      // conséquence en bêta : un import qui rate, c'est un utilisateur qui ne
      // revient pas, et il ne le signalera pas.
      track({
        name: "upload_failed",
        properties: {
          kind: uploadKind,
          reason: answered ? "parse_failed" : uploadFailureReason(err),
          durationMs: Date.now() - startedAt,
        },
      });

      setError(err instanceof Error ? err.message : "Erreur lors de l'analyse");
      // Roll back to ready so the user can retry without losing their edits.
      setPhase("ready");
    }
  }, [
    upload,
    studio,
    // `isAuthenticated` etait deja lu plus bas, par `recordPendingClaim`, sans
    // figurer ici : la fermeture pouvait donc retenir une valeur perimee et
    // enregistrer une revendication en attente pour quelqu un qui venait de se
    // connecter. Le garde-fou ci-dessus le lit aussi, d ou la correction.
    isAuthenticated,
    openAccountModal,
    title,
    artist,
    tempo,
    suggestedContext,
    selectedTracks,
    inspectResult,
    onSongLoaded,
    onClose,
    resetState,
  ]);

  /**
   * Active "help" view sits between the drop and the ready surfaces. It's
   * visible iff the inspect step published a `needs_help` payload with an
   * audition.
   */
  const showHelpScreen =
    phase === "ready" &&
    mode === "score" &&
    inspectResult !== null &&
    inspectResult.quality.kind === "needs_help" &&
    inspectResult.audition !== undefined;

  const handleHelpContinue = useCallback(
    (selection: number[]) => {
      // The help screen yields positions into the audition track list; map them
      // back to real MIDI track indices (what the analyzer's `tracks=` filter
      // expects) and carry them forward to the analyze call.
      const auditionTracks = inspectResult?.audition?.tracks ?? [];
      const trackIndices = selection
        .map((pos) => auditionTracks[pos]?.index)
        .filter((idx): idx is number => idx !== undefined);

      setSelectedTracks(trackIndices.length > 0 ? trackIndices : null);

      setInspectResult((prev) => {
        if (!prev) {
          return prev;
        }

        // Promote the quality verdict so the help screen unmounts but the
        // hints (if any) are still surfaced as a warning on the meta step.
        if (prev.quality.kind === "needs_help") {
          return {
            ...prev,
            quality: { kind: "ok_with_hints", hints: prev.quality.flags },
          };
        }

        return prev;
      });
    },
    [inspectResult],
  );

  const handleHelpBypass = useCallback(() => {
    setInspectResult((prev) => {
      if (!prev) {
        return prev;
      }

      if (prev.quality.kind === "needs_help") {
        return {
          ...prev,
          quality: { kind: "ok_with_hints", hints: prev.quality.flags },
        };
      }

      return prev;
    });
  }, []);

  const handleHelpChooseAnother = useCallback(() => {
    // Émis avant de vider l'état : c'est le dernier instant où l'on sait de
    // quel import on parle.
    if (importId !== null && inspectResult && inspectResult.quality.kind === "needs_help") {
      trackAbandoned({
        fileId: importId,
        reason: resolveNeedsHelpReason(inspectResult.quality.flags),
        exitVia: "retry",
        msSinceShown: 0,
      });
    }

    setUpload(null);
    setError(null);
    setDetectedKey(null);
    setInspectResult(null);
    setPhase("drop");
  }, [inspectResult, importId]);

  const handleBack = useCallback(() => {
    // From `ready` (or stale `analyzing`), back jumps to the drop zone.
    handleClear();
  }, [handleClear]);

  // ─── Derived rendering ───

  const isOcrDetails = phase === "ready" && mode === "images";
  const modalMaxHeight = isOcrDetails ? "min(90vh, 780px)" : "85vh";
  // Before a file is picked, the drop zone accepts every family, so the title
  // must not commit to one.
  const modalTitle =
    upload === null ? "Importer un morceau" : MODAL_TITLES[uploadSource(upload)];

  const stepCurrent = phase === "drop" ? 0 : 1;
  const hideHeader = phase === "launching";
  const isLaunching = phase === "launching";

  // Quality info routed to the meta card. Already scoped to the format, so a
  // hint reaching here is one this file can actually be guilty of.
  const qualityHints =
    inspectResult?.quality.kind === "ok_with_hints"
      ? inspectResult.quality.hints
      : undefined;
  const qualityMessage = qualityHints
    ? resolveOkWithHintsToastMessage(qualityHints)
    : null;
  const qualityScore = resolveQualityScore(inspectResult);

  // Footer copy / enablement
  const ctaEnabled = phase === "ready" && upload !== null;
  const ctaLabel =
    phase === "analyzing"
      ? "Analyse en cours…"
      : mode === "images"
        ? "Lancer l'OCR"
        : "Commencer";

  // Filename / size for the meta card.
  const filename =
    upload?.mode === "score"
      ? upload.file.name
      : upload?.mode === "images"
        ? upload.files[0]?.name ?? ""
        : "";
  const fileSize =
    upload?.mode === "score"
      ? formatFileSize(upload.file.size)
      : upload?.mode === "images"
        ? formatFileSize(
            upload.files.reduce((acc, f) => acc + f.size, 0),
          )
        : "";

  // The launching overlay covers the entire viewport; rendered outside the
  // modal shell so the cascade reads as a "Virtuosa takes over" moment.
  if (isLaunching) {
    return <LaunchingOverlay title={title} />;
  }

  return (
    <ModalTemplate
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxHeight={modalMaxHeight}
      header={hideHeader ? null : <StepDots steps={STEPS} current={stepCurrent} />}
      error={phase !== "drop" ? error : null}
      footer={
        phase === "drop" || showHelpScreen ? null : (
          <>
            <button
              type="button"
              onClick={handleBack}
              className="h-9 px-4 rounded-lg text-[12px] font-medium text-text-secondary hover:text-text-primary hover:bg-white/[0.04] transition-all duration-150 cursor-pointer"
            >
              Retour
            </button>

            <button
              type="button"
              onClick={handleAnalyze}
              disabled={!ctaEnabled}
              className="inline-flex items-center gap-2 px-5 rounded-full text-[13px] font-semibold transition-all duration-200 cursor-pointer"
              style={{
                height: 40,
                backgroundColor: ctaEnabled
                  ? "var(--color-accent)"
                  : "rgba(139, 92, 246, 0.10)",
                color: ctaEnabled
                  ? "rgba(255, 255, 255, 0.98)"
                  : "rgba(167, 139, 250, 0.40)",
                boxShadow: ctaEnabled
                  ? "0 0 20px rgba(139, 92, 246, 0.32), inset 0 1px 0 rgba(255, 255, 255, 0.18)"
                  : "none",
                cursor: ctaEnabled ? "pointer" : "not-allowed",
              }}
            >
              {ctaLabel}
              {ctaEnabled && <ArrowRight size={14} strokeWidth={2} aria-hidden />}
            </button>
          </>
        )
      }
    >
      {phase === "drop" && (
        <StepFile
          upload={upload}
          onScoreFile={handleScoreFile}
          onImageFiles={handleImageFiles}
          onUnsupportedFiles={handleUnsupportedFiles}
          onClear={handleClear}
          isLoading={false}
          error={error}
        />
      )}

      {showHelpScreen &&
        inspectResult &&
        upload?.mode === "score" &&
        importId !== null && (
          <UploadHelpScreen
            inspect={inspectResult}
            fileId={importId}
            onContinue={handleHelpContinue}
            onChooseAnother={handleHelpChooseAnother}
            onBypass={handleHelpBypass}
          />
        )}

      {(phase === "analyzing" || (phase === "ready" && !showHelpScreen)) &&
        upload && (
          <StepMeta
            phase={phase === "analyzing" ? "analyzing" : "ready"}
            source={uploadSource(upload)}
            filename={filename}
            fileSize={fileSize}
            title={title}
            artist={artist}
            tempo={tempo}
            detectedKey={detectedKey}
            qualityHints={qualityHints}
            qualityMessage={qualityMessage}
            quality={qualityScore}
            onTitle={handleTitleChange}
            onArtist={handleArtistChange}
            onTempo={setTempo}
            showOcrOptions={upload.mode === "images"}
            ocrTempoMissing={upload.mode === "images"}
            titleBadge={titleBadge}
            artistBadge={artistBadge}
            suggestedContext={suggestedContext}
            workRights={workRights}
          />
        )}
    </ModalTemplate>
  );
}

/**
 * Traduit un échec d'import en cause ventilable.
 *
 * `reason` recopiait `err.message` : des phrases françaises paramétrées, des
 * URLs d'infrastructure et des messages navigateur non normalisés partaient
 * chez un sous-traitant, sur une propriété de cardinalité non bornée que
 * personne ne pouvait regrouper. Le message reste affiché à l'écran, où il est
 * utile ; ce qui part est la décision qu'on prend derrière.
 *
 * Le refus de format y perd sa cause en route : le serveur la détaille dans
 * `errors.file` du corps RFC 7807, mais `ApiError` ne retient que `detail`, et
 * le 415 n'en a pas. On sait donc que le fichier a été refusé, pas ce qu'on lui
 * reproche.
 */
function uploadFailureReason(err: unknown): UploadFailureReason {
  if (!(err instanceof ApiError)) {
    // `fetch` ne rejette que sur le transport — réseau coupé, requête annulée,
    // CORS. Tout ce qui a une réponse HTTP passe par `ApiError`.
    return "network";
  }

  // 401 : la route image exige un compte. 403 : le compte est suspendu et ne
  // dépose plus. Les deux disent la même chose à qui lit l'entonnoir, et la
  // nuance se relit côté serveur.
  if (err.status === 401 || err.status === 403) {
    return "unauthorized";
  }

  if (err.status === 413) {
    return "too_large";
  }

  if (err.status === 415) {
    return "unsupported_format";
  }

  if (err.status === 429) {
    return "quota_reached";
  }

  if (err.status >= 500) {
    return "server";
  }

  return "unknown";
}

/**
 * Maps the inspect verdict to a 1..5 score for the `QualityDiamonds`.
 *
 * The analyzer doesn't (yet) return a numeric quality score, so we project
 * the discrete `kind` onto the diamonds: full bar for `ok`, three-fifths
 * for `ok_with_hints`, two-fifths for `needs_help` (the latter is rarely
 * shown by the meta step thanks to the help screen).
 */
function resolveQualityScore(inspect: InspectResult | null): number | undefined {
  if (!inspect) {
    return undefined;
  }

  if (inspect.quality.kind === "ok") {
    return 5;
  }

  if (inspect.quality.kind === "ok_with_hints") {
    return 3;
  }

  return 2;
}
